// Vendor Program v2 PR 3 — draft VFI invoice pool. Drafts accrue from
// accepted monthly reports (sourceReportId) or manual creation; staff
// select + batch-issue, or act per row (Issue / Void). A row click opens
// the page-owned audit drawer (?draft=<id>); the Source link opens the
// source report's review drawer (?report=<id>).
//
// NOTE: the wire DTO carries no createdAt, so there is no "Created"
// column — due date (server-defaulted at issue) renders instead.

import { useMemo, useState } from "react";
import {
  Button,
  Card,
  EmptyState,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { formatCalendarDate } from "@/utils/datetime";
import { useVendorInvoices } from "@/hooks/portfolioAdmin/useVendorBilling";
import type { BatchIssueResultDto, VendorInvoiceDto } from "@/api/portfolioAdmin/types";
import { fmtMoney } from "../billing/vendorBillingFormat";
import { ContractCheckChip } from "./ContractCheckPanel";
import { IssueDraftsConfirmModal } from "./IssueDraftsConfirmModal";
import { VoidDraftModal } from "./VoidDraftModal";

interface DraftInvoicesSectionProps {
  onOpenDraft: (invoiceId: string) => void;
  onOpenReport: (reportId: string) => void;
}

export function DraftInvoicesSection({
  onOpenDraft,
  onOpenReport,
}: DraftInvoicesSectionProps) {
  const auth = useAuth();
  const draftsQuery = useVendorInvoices({ status: "draft" });
  const drafts = useMemo(() => draftsQuery.data ?? [], [draftsQuery.data]);

  const canIssue =
    auth.hasPermission("VendorBilling.Issue") || auth.hasRoleCode("ADMIN");
  const canVoid = auth.hasPermission("VendorBilling.Void");

  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [issueTarget, setIssueTarget] = useState<VendorInvoiceDto[] | null>(null);
  const [voidTarget, setVoidTarget] = useState<VendorInvoiceDto | null>(null);

  const selected = drafts.filter((d) => selectedIds.has(d.id));
  const allSelected = drafts.length > 0 && selected.length === drafts.length;

  const toggleAll = (checked: boolean) =>
    setSelectedIds(checked ? new Set(drafts.map((d) => d.id)) : new Set());

  const toggleId = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Keep the per-item failures selected for an easy retry; issued rows
  // leave the draft pool on invalidation anyway.
  const onIssued = (result: BatchIssueResultDto) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      result.issued.forEach((i) => next.delete(i.id));
      return next;
    });

  return (
    <Card data-testid="vfi-drafts-section">
      <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-3">
        <h3 className="font-display font-bold text-h3 m-0">Draft invoices</h3>
        {canIssue && (
          <Button
            variant="primary"
            size="sm"
            disabled={selected.length === 0}
            onClick={() => setIssueTarget(selected)}
            data-testid="vfi-issue-selected"
          >
            Issue selected ({selected.length})
          </Button>
        )}
      </div>

      {draftsQuery.isLoading && (
        <div className="flex flex-col gap-space-2" data-testid="vfi-drafts-loading">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {draftsQuery.isError && (
        <div data-testid="vfi-drafts-error">
          <p className="text-small text-status-error m-0" role="alert">
            Could not load the draft invoice pool.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-space-2"
            onClick={() => void draftsQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      )}

      {draftsQuery.isSuccess && drafts.length === 0 && (
        <div data-testid="vfi-drafts-empty">
          <EmptyState
            variant="empty"
            title="No draft invoices"
            description="Accepting a monthly vendor report generates a draft here for review before sending."
          />
        </div>
      )}

      {draftsQuery.isSuccess && drafts.length > 0 && (
        <Table data-testid="vfi-drafts-table">
          <THead>
            <TR>
              <TH className="text-white-pure w-[24px]">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => toggleAll(e.target.checked)}
                  aria-label="Select all drafts"
                  data-testid="vfi-drafts-select-all"
                />
              </TH>
              <TH className="text-white-pure">Invoice #</TH>
              <TH className="text-white-pure">Vendor</TH>
              <TH className="text-white-pure">Loan</TH>
              <TH className="text-white-pure text-right">Amount</TH>
              <TH className="text-white-pure">Contract check</TH>
              <TH className="text-white-pure">Source</TH>
              <TH className="text-white-pure">Due</TH>
              <TH className="text-white-pure">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {drafts.map((d) => (
              <DraftRow
                key={d.id}
                invoice={d}
                selected={selectedIds.has(d.id)}
                canIssue={canIssue}
                canVoid={canVoid}
                onToggle={() => toggleId(d.id)}
                onOpen={() => onOpenDraft(d.id)}
                onOpenReport={onOpenReport}
                onIssue={() => setIssueTarget([d])}
                onVoid={() => setVoidTarget(d)}
              />
            ))}
          </TBody>
        </Table>
      )}

      {issueTarget && (
        <IssueDraftsConfirmModal
          invoices={issueTarget}
          open
          onClose={() => setIssueTarget(null)}
          onIssued={onIssued}
        />
      )}
      {voidTarget && (
        <VoidDraftModal
          invoice={voidTarget}
          open
          onClose={() => setVoidTarget(null)}
        />
      )}
    </Card>
  );
}

function DraftRow({
  invoice,
  selected,
  canIssue,
  canVoid,
  onToggle,
  onOpen,
  onOpenReport,
  onIssue,
  onVoid,
}: {
  invoice: VendorInvoiceDto;
  selected: boolean;
  canIssue: boolean;
  canVoid: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onOpenReport: (reportId: string) => void;
  onIssue: () => void;
  onVoid: () => void;
}) {
  return (
    <TR
      onClick={onOpen}
      className="cursor-pointer"
      data-testid={`vfi-draft-row-${invoice.id}`}
    >
      <TD onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select invoice ${invoice.invoiceNumber}`}
          data-testid={`vfi-draft-checkbox-${invoice.id}`}
        />
      </TD>
      <TD className="whitespace-nowrap">{invoice.invoiceNumber}</TD>
      <TD>
        {invoice.vendorName}
        {invoice.audience === "borrower" && (
          <span
            className="block text-caption text-gray-mid"
            data-testid={`vfi-draft-borrower-billed-${invoice.id}`}
          >
            Bills the borrower
          </span>
        )}
      </TD>
      <TD>{invoice.loanNumber}</TD>
      <TD className="text-right tabular-nums">{fmtMoney(invoice.amount)}</TD>
      <TD>
        <ContractCheckChip comparison={invoice.contractComparison} />
      </TD>
      <TD onClick={(e) => e.stopPropagation()}>
        {invoice.sourceReportId ? (
          <button
            type="button"
            className="text-gold-shadow underline hover:text-onyx"
            onClick={() => onOpenReport(invoice.sourceReportId!)}
            data-testid={`vfi-draft-source-${invoice.id}`}
          >
            Report
          </button>
        ) : (
          <span className="text-gray-mid">Manual</span>
        )}
      </TD>
      <TD className="whitespace-nowrap">
        {invoice.dueDate ? (
          formatCalendarDate(invoice.dueDate)
        ) : (
          // Drafts usually have no due date yet (server defaults at issue).
          <span className="text-gray-mid">At issue + 30d</span>
        )}
      </TD>
      <TD onClick={(e) => e.stopPropagation()}>
        <div className="flex gap-space-2">
          {canIssue && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onIssue}
              data-testid={`vfi-draft-issue-${invoice.id}`}
            >
              Issue
            </Button>
          )}
          {canVoid && (
            <Button
              variant="destructive"
              size="sm"
              onClick={onVoid}
              data-testid={`vfi-draft-void-${invoice.id}`}
            >
              Void
            </Button>
          )}
        </div>
      </TD>
    </TR>
  );
}
